import { DestinationCGType } from '@/types/graphql/root';
import React from 'react';
import 'dayjs/locale/ko';
import { ReactSortable, Sortable } from 'react-sortablejs';
import ArrowRight232323812 from '@svg/trip/arrow_right_232323_8_12.svg';
import DestSlot from '@/app/ui/trip/dest-slot';
import AddGray1818 from '@svg/trip/add_gray_18_18.svg';
import { useAtom, useSetAtom } from 'jotai';
import { isEditRoleAtom } from '@/atom/trip';
import { usePathname } from 'next/navigation';
import { history } from '@/utils/history';
import { historyStackAtom } from '@/atom/historyStack';
import { useRouter } from 'next/navigation';
import { TRIP_CONDITIONS, TRIP_MODE, TripModeType } from '@/app/ui/trip/client-trip-page';

export interface TripItineraryViewProps {
  destinationsByDay: {
    [key: number]: {
      morningAccomm?: {
        prev: DestinationCGType | null;
        cur: DestinationCGType;
        next: DestinationCGType | null;
      };
      nightAccomm?: {
        prev: DestinationCGType | null;
        cur: DestinationCGType;
        next: DestinationCGType | null;
      };
      destinationsWithoutAccomm: {
        prev: DestinationCGType | null;
        cur: DestinationCGType;
        next: DestinationCGType | null;
      }[];
    };
  };
  destinations: DestinationCGType[];
  focusedDay: number;
  memoDictionary: { [key: string]: string };
  selectedDestIdList: string[];
  isSortableDragging: boolean;
  mode: TripModeType;
  isEmbedded: boolean;
  getDayInfo: (day: number) => string | null;
  setDestinations: React.Dispatch<React.SetStateAction<DestinationCGType[]>>;
  onSortableDragEnd: (e: Sortable.SortableEvent) => Promise<void>;
  setIsSortableDragging: React.Dispatch<React.SetStateAction<boolean>>;
  onClickPoiDetail: (dest: DestinationCGType) => void;
  onClickChangeMode: (nextMode: TripModeType) => Promise<void>;
  setAddDestinationMode: React.Dispatch<React.SetStateAction<'selectDay' | 'normal'>>;
  setAddDestinationsPosition: React.Dispatch<
    React.SetStateAction<{
      day: number;
      pos: number;
      replace?: boolean | undefined;
      replaceDestId?: string | undefined;
    } | null>
  >;
  onClickRemoveSlot: (destId: string) => void;
  onToggleSelectDest: (destId: string) => void;
  onUpdateMemo: (destId: string, memo: string) => void;
  setSelectDayModalVisible: React.Dispatch<React.SetStateAction<boolean>>;
}

export default function TripItineraryView({
  destinationsByDay,
  destinations,
  focusedDay,
  memoDictionary,
  selectedDestIdList,
  isSortableDragging,
  mode,
  isEmbedded,
  getDayInfo,
  setDestinations,
  onSortableDragEnd,
  setIsSortableDragging,
  onClickPoiDetail,
  onClickChangeMode,
  setAddDestinationMode,
  setAddDestinationsPosition,
  onClickRemoveSlot,
  onToggleSelectDest,
  onUpdateMemo,
  setSelectDayModalVisible,
}: TripItineraryViewProps) {
  const [isEditRole] = useAtom(isEditRoleAtom);
  const pathname = usePathname();
  const setHistoryStack = useSetAtom(historyStackAtom);
  const router = useRouter();

  return (
    <div id='dest-container'>
      {Object.keys(destinationsByDay).map((dayNumber, dayIndex) => {
        const isLastDay = dayIndex === Object.keys(destinationsByDay).length - 1;
        const isEmptyDay =
          !destinationsByDay[+dayNumber].morningAccomm &&
          !destinationsByDay[+dayNumber].nightAccomm &&
          destinationsByDay[+dayNumber].destinationsWithoutAccomm.length === 0;

        const morningAccomm = destinationsByDay[+dayNumber].morningAccomm;
        const nightAccomm = destinationsByDay[+dayNumber].nightAccomm;
        const destListWithoutAccomms = destinationsByDay[+dayNumber].destinationsWithoutAccomm;

        return (
          <React.Fragment key={'dayBox_' + dayNumber}>
            <div
              className={`sticky bg-white z-[2] ${TRIP_CONDITIONS.IS_VIEW_OR_MEMO_MODE(mode) ? 'top-0' : 'top-52pxr'}`}
              style={{
                zIndex: 999 - +dayNumber,
              }}
            >
              <div
                id={'dayHeader_' + dayNumber}
                key={mode + '_dayHeader_' + dayNumber}
                className={`day-header-button flex flex-row items-center w-full py-10pxr px-20pxr z-[1] justify-between ${
                  TRIP_CONDITIONS.IS_VIEW_OR_MEMO_MODE(mode) ? 'bg-gray-100' : 'bg-point-100'
                }`}
              >
                <button
                  className='flex items-center h-22pxr'
                  onClick={() => {
                    if (history) {
                      history.push(pathname + '#select-day');
                      setHistoryStack((prev) => [
                        ...prev,
                        {
                          name: 'SelectDayModal 종료 (from client-trip-page.tsx)',
                          afterAction: () => setSelectDayModalVisible(false),
                        },
                      ]);
                      setSelectDayModalVisible(true);
                    }
                  }}
                >
                  {+dayNumber === 1 ? (
                    <>
                      <span className='sub-title-14-bold text-custom-black mr-6pxr'>{focusedDay}일차</span>
                      <span className='text-gray-700 body-14-regular mr-4pxr'>
                        {getDayInfo(+dayNumber + focusedDay - 1)}
                      </span>
                    </>
                  ) : (
                    <>
                      <span className='sub-title-14-bold text-custom-black mr-6pxr'>{dayNumber}일차</span>
                      <span className='text-gray-700 body-14-regular mr-4pxr'>{getDayInfo(+dayNumber)}</span>
                    </>
                  )}
                  <span className='w-20pxr h-20pxr flex items-center justify-center'>
                    <ArrowRight232323812 className='flex-none' />
                  </span>
                </button>
                {isEditRole && !isEmbedded && 1 === +dayNumber && !TRIP_CONDITIONS.IS_MEMO_MODE(mode) && (
                  <div className='flex items-center h-22pxr'>
                    <button
                      className='mr-12pxr sub-heading-14-m text-custom-black py-2pxr px-4pxr'
                      onClick={() => {
                        onClickChangeMode(TRIP_MODE.MEMO);
                      }}
                    >
                      메모
                    </button>
                    <div className='h-14pxr bg-gray-400 w-1pxr'></div>
                    <button
                      className='ml-12pxr sub-heading-14-m text-primary py-2pxr px-4pxr'
                      onClick={() => {
                        if (TRIP_CONDITIONS.IS_VIEW_MODE(mode)) {
                          onClickChangeMode(TRIP_MODE.EDIT);
                        } else {
                          router.back();
                        }
                      }}
                    >
                      {TRIP_CONDITIONS.IS_VIEW_MODE(mode) ? '편집' : '보기'}
                    </button>
                  </div>
                )}
              </div>
            </div>

            <div
              id={`dayContainer_${dayNumber}`}
              className={`relative flex flex-col ${TRIP_CONDITIONS.IS_VIEW_MODE(mode) ? 'pt-20pxr pb-28pxr' : 'pt-24pxr pb-32pxr'}`}
              style={isLastDay ? { paddingBottom: 0 } : {}}
            >
              {TRIP_CONDITIONS.IS_VIEW_MODE(mode) && !isEmptyDay && (
                <div className='absolute top-0 bottom-0 bg-gray-300 w-1pxr left-42pxr'></div>
              )}

              {morningAccomm && (
                <DestSlot
                  key={morningAccomm.cur.id || `morning_day${dayNumber}`}
                  dest={morningAccomm}
                  mode={mode}
                  dayNumber={+dayNumber}
                  destIndex={0}
                  isEditRole={isEditRole}
                  isLastDay={isLastDay}
                  isLastDest={destListWithoutAccomms.length === 0 && !nightAccomm}
                  onClickPoiDetail={onClickPoiDetail}
                  setDestinations={setDestinations}
                  isSortableDragging={isSortableDragging}
                  destinations={destinations}
                  onClickChangeMode={onClickChangeMode}
                  setAddDestinationMode={setAddDestinationMode}
                  setAddDestinationsPosition={setAddDestinationsPosition}
                  onClickRemoveSlot={onClickRemoveSlot}
                  onToggleSelectDest={onToggleSelectDest}
                  onUpdateMemo={onUpdateMemo}
                  selectedDestIdList={selectedDestIdList}
                  memoDictionary={memoDictionary}
                />
              )}
              <div data-day={dayNumber}>
                <ReactSortable
                  className={`${isSortableDragging ? 'sortable-container' : ''}`}
                  group='dest-list'
                  animation={150}
                  handle='.dest-handle'
                  scroll={false}
                  scrollSensitivity={120}
                  scrollSpeed={12}
                  ghostClass='dest-ghost'
                  dragoverBubble={false}
                  forceFallback={true}
                  list={destListWithoutAccomms.map((dest) => {
                    return {
                      ...dest,
                      id: dest.cur.id ?? '',
                    };
                  })}
                  setList={(newList) => {}}
                  onChoose={(e: any) => {
                    setIsSortableDragging(true);
                  }}
                  onUnchoose={() => {
                    setIsSortableDragging(false);
                  }}
                  onEnd={(e) => {
                    setIsSortableDragging(false);
                    if (e.from === e.to && e.oldIndex === e.newIndex) return;
                    onSortableDragEnd(e);
                  }}
                >
                  {destListWithoutAccomms.map((dest, destIndex) => (
                    <DestSlot
                      key={dest.cur.id || `dest_day${dayNumber}_${destIndex}`}
                      dest={dest}
                      mode={mode}
                      dayNumber={+dayNumber}
                      destIndex={destIndex + (morningAccomm ? 1 : 0)}
                      isEditRole={isEditRole}
                      isLastDay={isLastDay}
                      isLastDest={destIndex === destListWithoutAccomms.length - 1 && !nightAccomm}
                      onClickPoiDetail={onClickPoiDetail}
                      setDestinations={setDestinations}
                      isSortableDragging={isSortableDragging}
                      destinations={destinations}
                      onClickChangeMode={onClickChangeMode}
                      setAddDestinationMode={setAddDestinationMode}
                      setAddDestinationsPosition={setAddDestinationsPosition}
                      onClickRemoveSlot={onClickRemoveSlot}
                      onToggleSelectDest={onToggleSelectDest}
                      onUpdateMemo={onUpdateMemo}
                      selectedDestIdList={selectedDestIdList}
                      memoDictionary={memoDictionary}
                    />
                  ))}
                </ReactSortable>
              </div>
              {nightAccomm && (
                <DestSlot
                  key={nightAccomm.cur.id || `night_day${dayNumber}`}
                  dest={nightAccomm}
                  mode={mode}
                  dayNumber={+dayNumber}
                  destIndex={destListWithoutAccomms.length + (morningAccomm ? 1 : 0)}
                  isEditRole={isEditRole}
                  isLastDay={isLastDay}
                  isLastDest
                  onClickPoiDetail={onClickPoiDetail}
                  setDestinations={setDestinations}
                  isSortableDragging={isSortableDragging}
                  destinations={destinations}
                  onClickChangeMode={onClickChangeMode}
                  setAddDestinationMode={setAddDestinationMode}
                  setAddDestinationsPosition={setAddDestinationsPosition}
                  onClickRemoveSlot={onClickRemoveSlot}
                  onToggleSelectDest={onToggleSelectDest}
                  onUpdateMemo={onUpdateMemo}
                  selectedDestIdList={selectedDestIdList}
                  memoDictionary={memoDictionary}
                />
              )}

              {isEditRole && isEmptyDay && (
                <div className={`px-20pxr z-[1] ${TRIP_CONDITIONS.IS_VIEW_MODE(mode) ? 'py-4pxr' : 'py-0'}`}>
                  <button
                    className='flex items-center justify-center w-full bg-white border border-gray-200 h-40pxr gap-4pxr rounded-4pxr'
                    onClick={() => {
                      onClickChangeMode(TRIP_MODE.SEARCH);
                      setAddDestinationsPosition({
                        day: +dayNumber,
                        pos: 1,
                      });
                      setAddDestinationMode('normal');
                    }}
                  >
                    <AddGray1818 className='flex-none' />
                    <span className='text-gray-700 body-14-r'>장소 추가</span>
                  </button>
                </div>
              )}
            </div>
          </React.Fragment>
        );
      })}
    </div>
  );
}
